/**
 * Animates one value on a spring from `from` (moving at `velocity` per second) to `to`, calling
 * `set` each frame and `done` once it rests. `stop()` halts it where it is and reports its value
 * and velocity, so a finger or a new target can take over mid-flight. `damping` 1 arrives
 * without overshoot; below 1 it bounces a little. `response` is roughly how long it takes, in
 * seconds.
 */
export function springTo({
  from,
  to,
  velocity = 0,
  damping = 1,
  response = 0.35,
  set,
  done,
}: {
  from: number;
  to: number;
  velocity?: number;
  damping?: number;
  response?: number;
  set: (value: number) => void;
  done?: () => void;
}): { stop: () => { value: number; velocity: number } } {
  const w = (2 * Math.PI) / response;
  let x = from;
  let v = velocity;
  let last: number | null = null;
  let frame = requestAnimationFrame(function step(now) {
    const dt = last === null ? 1 / 60 : Math.min(0.05, (now - last) / 1000);
    last = now;
    for (let i = 0; i < 4; i++) {
      v += ((-w * w * (x - to) - 2 * damping * w * v) * dt) / 4;
      x += (v * dt) / 4;
    }
    if (Math.abs(x - to) < 0.3 && Math.abs(v) < 8) {
      x = to;
      v = 0;
      set(to);
      done?.();
      return;
    }
    set(x);
    frame = requestAnimationFrame(step);
  });
  return {
    stop: () => {
      cancelAnimationFrame(frame);
      return { value: x, velocity: v };
    },
  };
}
